import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Sidebar } from "@/components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Edit2, Save, X, DollarSign, Users, Star, Crown, Zap, Plus, Trash2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

interface SubscriptionPlan {
  id: number;
  name: string;
  description: string | null;
  monthlyPrice: string;
  yearlyPrice: string;
  features: string[] | null;
  isActive: boolean;
  isPublic: boolean;
}

export default function FriendlySubscriptionPlansPage() {
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subscription plans
  const { data: rawPlans, isLoading, refetch } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const response = await fetch("/api/subscription-plans/all", { credentials: "include" });
      if (!response.ok) return [];
      const data = await response.json();
      return data;
    }
  });

  const plans = Array.isArray(rawPlans) ? rawPlans : [];

  // Fetch subscriptions for metrics
  const { data: rawSubscriptions = [] } = useQuery({
    queryKey: ["admin-subscriptions"],
    queryFn: () => fetch("/api/admin/subscriptions", { credentials: "include" }).then(r => r.json())
  });
  const subscriptions = Array.isArray(rawSubscriptions) ? rawSubscriptions : [];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updatedPlan: SubscriptionPlan) => {
      return await apiRequest("PATCH", `/api/admin/subscription-plans/${updatedPlan.id}`, updatedPlan);
    },
    onSuccess: () => {
      toast({ 
        title: "🎉 Success!", 
        description: "Subscription plan updated successfully!",
      });
      setEditingPlan(null);
      queryClient.invalidateQueries({ queryKey: ["subscription-plans"] });
    },
    onError: (error: any) => {
      toast({ 
        title: "❌ Error", 
        description: `Failed to update: ${error.message}`, 
        variant: "destructive" 
      });
    }
  });

  const getPlanIcon = (planName: string) => {
    if (planName.toLowerCase().includes('seed')) return <Star className="h-5 w-5 text-green-500" />;
    if (planName.toLowerCase().includes('ledger')) return <Zap className="h-5 w-5 text-blue-500" />;
    if (planName.toLowerCase().includes('grow')) return <Crown className="h-5 w-5 text-purple-500" />;
    return <DollarSign className="h-5 w-5 text-gray-500" />;
  };

  const getMetrics = (planId: number) => {
    const planSubs = subscriptions.filter((s: any) => s.planId === planId && s.status === 'active');
    const subscribers = planSubs.length;
    const plan = plans.find((p: any) => p.id === planId);
    const revenue = subscribers * parseFloat(plan?.monthlyPrice || '0');
    return { subscribers, revenue };
  };

  const startEdit = (plan: SubscriptionPlan) => {
    setEditingPlan({ 
      ...plan,
      description: plan.description || '',
      features: plan.features || []
    });
  };

  const cancelEdit = () => {
    setEditingPlan(null);
  };

  const saveEdit = () => {
    if (editingPlan) {
      updateMutation.mutate(editingPlan);
    }
  };

  const updateEditingPlan = (field: keyof SubscriptionPlan, value: any) => {
    if (editingPlan) {
      setEditingPlan({ ...editingPlan, [field]: value });
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <main className="flex-1 overflow-auto p-8 ml-64">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <div className="text-lg font-medium">Loading your subscription plans...</div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <main className="flex-1 overflow-auto p-8 ml-64">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header Section */}
          <div className="text-center">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Subscription Plans Management
            </h1>
            <p className="text-lg text-muted-foreground mt-2">
              Manage your ZentroBooks pricing plans with ease
            </p>
            <div className="flex justify-center gap-4 mt-6">
              <Button onClick={() => refetch()} variant="outline" className="px-6">
                🔄 Refresh Data
              </Button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {plans.map((plan: SubscriptionPlan) => {
              const isEditing = editingPlan?.id === plan.id;
              const { subscribers, revenue } = getMetrics(plan.id);

              return (
                <Card key={plan.id} className={`relative transition-all duration-300 hover:shadow-lg ${
                  isEditing ? "ring-2 ring-blue-500 shadow-xl" : ""
                } ${plan.name.toLowerCase().includes('grow') ? 'border-purple-200 bg-gradient-to-br from-purple-50 to-white' : 
                   plan.name.toLowerCase().includes('ledger') ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' :
                   'border-green-200 bg-gradient-to-br from-green-50 to-white'}`}>
                  
                  {/* Popular Badge for ZentroGrow */}
                  {plan.name.toLowerCase().includes('grow') && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-purple-500 text-white px-3 py-1">
                        ⭐ Most Popular
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pb-4">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      {getPlanIcon(plan.name)}
                      <CardTitle className="text-2xl font-bold">
                        {isEditing ? (
                          <Input
                            value={editingPlan.name}
                            onChange={(e) => updateEditingPlan('name', e.target.value)}
                            className="text-2xl font-bold text-center border-2 border-blue-300"
                          />
                        ) : (
                          plan.name
                        )}
                      </CardTitle>
                    </div>
                    
                    {/* Pricing Section */}
                    <div className="space-y-4">
                      <div className="text-center">
                        {isEditing ? (
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm font-medium block mb-1">Monthly Price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl">$</span>
                                <Input
                                  value={editingPlan.monthlyPrice}
                                  onChange={(e) => updateEditingPlan('monthlyPrice', e.target.value)}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="pl-8 text-xl font-bold text-center"
                                />
                              </div>
                            </div>
                            <div>
                              <label className="text-sm font-medium block mb-1">Yearly Price</label>
                              <div className="relative">
                                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-xl">$</span>
                                <Input
                                  value={editingPlan.yearlyPrice}
                                  onChange={(e) => updateEditingPlan('yearlyPrice', e.target.value)}
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  className="pl-8 text-xl font-bold text-center"
                                />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="text-4xl font-bold text-gray-900 mb-1">
                              ${plan.monthlyPrice}
                            </div>
                            <div className="text-sm text-gray-600 mb-3">per month</div>
                            <div className="text-lg font-semibold text-blue-600">
                              ${plan.yearlyPrice}/year
                            </div>
                            {plan.monthlyPrice && plan.yearlyPrice && (
                              <div className="text-sm text-green-600 font-medium mt-1">
                                💰 Save ${((parseFloat(plan.monthlyPrice) * 12) - parseFloat(plan.yearlyPrice)).toFixed(0)} annually
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Description */}
                    <div>
                      {isEditing ? (
                        <div>
                          <label className="text-sm font-medium block mb-2">Description</label>
                          <textarea
                            value={editingPlan.description || ''}
                            onChange={(e) => updateEditingPlan('description', e.target.value)}
                            className="w-full h-20 p-3 border-2 border-gray-200 rounded-lg text-sm resize-none focus:border-blue-400"
                            placeholder="Enter plan description..."
                          />
                        </div>
                      ) : (
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {plan.description}
                        </p>
                      )}
                    </div>

                    {/* Features */}
                    <div>
                      {isEditing ? (
                        <div>
                          <label className="text-sm font-medium block mb-2">Features (one per line)</label>
                          <textarea
                            value={editingPlan.features ? editingPlan.features.join('\n') : ''}
                            onChange={(e) => updateEditingPlan('features', e.target.value.split('\n').filter(f => f.trim()))}
                            className="w-full h-32 p-3 border-2 border-gray-200 rounded-lg text-sm resize-none focus:border-blue-400"
                            placeholder="Basic bookkeeping&#10;Transaction import&#10;Financial reports"
                          />
                        </div>
                      ) : (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-3">✨ Features Included:</h4>
                          <ul className="space-y-2">
                            {(plan.features || []).map((feature, index) => (
                              <li key={index} className="flex items-start gap-2 text-sm">
                                <span className="text-green-500 mt-0.5">✓</span>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Metrics */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold text-blue-600">{subscribers}</div>
                          <div className="text-xs text-gray-500">Subscribers</div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-green-600">${revenue.toFixed(0)}</div>
                          <div className="text-xs text-gray-500">Monthly Revenue</div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-4">
                      {isEditing ? (
                        <>
                          <Button 
                            onClick={saveEdit} 
                            disabled={updateMutation.isPending}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            {updateMutation.isPending ? "Saving..." : "Save Changes"}
                          </Button>
                          <Button 
                            onClick={cancelEdit} 
                            variant="outline"
                            className="flex-1"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button 
                          onClick={() => startEdit(plan)} 
                          variant="outline"
                          className="w-full hover:bg-blue-50"
                        >
                          <Edit2 className="h-4 w-4 mr-2" />
                          Edit Plan
                        </Button>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="flex justify-center">
                      <Badge 
                        variant={plan.isActive ? "default" : "secondary"}
                        className={plan.isActive ? "bg-green-500" : ""}
                      >
                        {plan.isActive ? "🟢 Active" : "⭕ Inactive"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Summary Stats */}
          <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
            <CardHeader>
              <CardTitle className="text-center text-xl">📊 Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{plans.length}</div>
                  <div className="text-sm text-gray-600">Total Plans</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-600">
                    {subscriptions.filter((s: any) => s.status === 'active').length}
                  </div>
                  <div className="text-sm text-gray-600">Active Subscribers</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-purple-600">
                    ${subscriptions.reduce((total: number, sub: any) => {
                      if (sub.status === 'active') {
                        const plan = plans.find((p: any) => p.id === sub.planId);
                        return total + parseFloat(plan?.monthlyPrice || '0');
                      }
                      return total;
                    }, 0).toFixed(0)}
                  </div>
                  <div className="text-sm text-gray-600">Monthly Revenue</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}